import React, { useCallback, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  SummaryState,
  GroupingState,
  IntegratedGrouping,
  IntegratedSummary,
  SortingState,
  IntegratedSorting,
  SelectionState,
  IntegratedSelection,
  FilteringState,
  IntegratedFiltering
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableGroupRow,
  TableSummaryRow,
  TableColumnResizing,
  ExportPanel,
  Toolbar,
  TableSelection
} from '@devexpress/dx-react-grid-material-ui';
import PropTypes from 'prop-types';

import { format, parse } from 'date-fns';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import saveAs from 'file-saver';
import HeaderFilterCell from './HeaderFilterCell';

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

const GroupCellContent = ({ row }) => <span>{row.value}</span>;

GroupCellContent.propTypes = {
  row: PropTypes.object
};

const ReportTable = ({ rows }) => {
  const [columns] = useState([
    {
      name: 'DisplayName',
      title: 'DisplayName',
      getCellValue: (row) => (row.EmployeeRef ? row.EmployeeRef.name : undefined)
    },
    {
      name: 'Date',
      title: 'Date',
      getCellValue: (row) => {
        if (row.Date) {
          const dateFormat = format(new Date(row.Date), 'dd/MM/yyyy');
          return dateFormat;
        }
      }
    },
    { name: 'StartTime', title: 'StartTime' },
    { name: 'FinishTime', title: 'FinishTime' },
    {
      name: 'ClassRef',
      title: 'ClassRef'
    },
    {
      name: 'Description',
      title: 'Comments'
    },

    {
      name: 'Hours',
      title: 'Hours',
      getCellValue: (row) => {
        if (row.StartTime && row.FinishTime) {
          const startDateMs = parse(row.StartTime, 'HH:mm', new Date(row.Date));
          const endDateMs = parse(row.FinishTime, 'HH:mm', new Date(row.Date));

          const diffHr = Math.abs((endDateMs - startDateMs) / (1000 * 60 * 60)).toFixed(1);

          return Number(diffHr);
        }
      }
    }
  ]);

  const [columnWidths, setColumnWidths] = useState([
    { columnName: 'DisplayName', width: 180 },
    { columnName: 'Date', width: 180 },
    { columnName: 'StartTime', width: 150 },
    { columnName: 'FinishTime', width: 150 },
    { columnName: 'ClassRef', width: 180 },
    { columnName: 'Description', width: 180 },
    { columnName: 'Hours', width: 180 }
  ]);

  const [totalSummaryItems] = useState([
    { columnName: 'DisplayName', type: 'count' },
    { columnName: 'Hours', type: 'sum' }
  ]);

  const [grouping] = useState([{ columnName: 'DisplayName' }]);
  const [groupSummaryItems] = useState([
    { columnName: 'DisplayName', type: 'count' },
    { columnName: 'Hours', type: 'sum' },
    {
      columnName: 'Hours',
      type: 'sum',
      showInGroupFooter: false,
      alignByColumn: true
    },
    {
      columnName: 'Date',
      type: 'count',
      showInGroupFooter: false,
      alignByColumn: true
    }
  ]);
  const [sorting, setSorting] = useState([{ columnName: 'DisplayName', direction: 'asc' }]);

  const [selection, setSelection] = useState([]);
  const [filters, setFilters] = useState([{ columnName: 'ClassRef', value: '' }]);

  const exporterRef = useRef(null);
  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const computedRows = rows?.map((row) => ({
    ...row,
    DisplayName: row?.EmployeeRef?.name || '',
    ClassRef: row?.ClassRef?.name || ''
  }));

  const classRefFilterPredicate = (value, filter) => {
    if (!filter) return true;
    return value === filter;
  };

  const customizeCell = (excelCell, row, column) => {
    if (column.name === 'Date' && row.Date) {
      excelCell.value = new Date(row.Date);
      excelCell.numFmt = 'dd/mm/yyyy';
    }
  };

  return (
    <Paper>
      <Grid rows={computedRows} columns={columns}>
        <SortingState sorting={sorting} onSortingChange={setSorting} />
        <IntegratedSorting />

        <FilteringState
          filters={filters}
          onFiltersChange={setFilters}
          columnExtensions={[{ columnName: 'ClassRef', predicate: classRefFilterPredicate }]}
        />
        <GroupingState grouping={grouping} />

        <SummaryState totalItems={totalSummaryItems} groupItems={groupSummaryItems} />
        <SelectionState selection={selection} onSelectionChange={setSelection} />

        <IntegratedFiltering />
        <IntegratedGrouping />
        <IntegratedSummary />
        <IntegratedSelection />

        <Table />
        <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
        <TableHeaderRow
          showSortingControls
          cellComponent={(props) => (
            <HeaderFilterCell {...props} rows={computedRows} filters={filters} onFiltersChange={setFilters} />
          )}
        />

        <TableSelection />
        <TableGroupRow showColumnsWhenGrouped contentComponent={GroupCellContent} />
        <TableSummaryRow />

        <Toolbar />
        <ExportPanel startExport={startExport} />
      </Grid>

      <GridExporter
        ref={exporterRef}
        rows={computedRows}
        columns={columns}
        totalSummaryItems={totalSummaryItems}
        groupSummaryItems={groupSummaryItems}
        selection={selection}
        onSave={onSave}
        sorting={sorting}
        filters={filters}
        customizeCell={customizeCell}
      />
    </Paper>
  );
};

export default ReportTable;

ReportTable.propTypes = {
  rows: PropTypes.any
};
